import { Combobox, ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { Popup } from "@/Common/Components/Modals/Popup";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";

interface IMedicalNoteDeleteWarningPopupProps {
    noteModel: IPatientMedicalNoteModel;
    medicalSummaryModel: IPatientMedicalSummaryModel;
}

class MedicalNoteDeleteWarningPopupState {
    public reasonCode: string;
    public details: string;
    public isLoading: boolean;
}

class MedicalNoteDeleteReasonPopup extends React.Component<IMedicalNoteDeleteWarningPopupProps, MedicalNoteDeleteWarningPopupState> {
    private id: string = "deletenotepopumenu";
    private noteDeletionReasons: string[] = [
        "NoteDeletionReasonDuplicateLabel",
        "NoteDeletionReasonWrongPatientFileLabel",
        "NoteDeletionReasonMistakeLabel",
        "NoteDeletionReasonOtherLabel"];

    constructor(props: IMedicalNoteDeleteWarningPopupProps) {
        super(props);

        const initialState = new MedicalNoteDeleteWarningPopupState();
        initialState.reasonCode = props.noteModel.get("DeleteReasonCode") || "NoteDeletionReasonMistakeLabel";
        initialState.details = props.noteModel.get("DeleteDetails") || "";
        initialState.isLoading = false;
        this.state = initialState;
    }

    public componentDidMount(): void {
        overlayStore.showOverlay(this.id);
    }

    public componentWillUnmount(): void {
        overlayStore.hideOverlay(this.id);
    }

    public render(): JSX.Element {
        let activeCode = this.state.reasonCode;
        let currentEncounter;
        let noteModel = this.props.noteModel;

        if (noteModel != null) {
            currentEncounter = noteModel.get("Encounter");
        }

        return (
            <Popup
                Id={this.id}
                title={cacheStore.GetString("WarningLabel")}
                onCloseDelegate={this.cancelAndClosePopup}
                draggable={true}
                width={550}
                centeredHorizontally={true}
            >
                <div className="p-body" data-cy="medicalNote-DeleteReason-popup">
                    <div className="row stack-buffer">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <strong className="red-text">
                                {cacheStore.GetString("EncounterAuditWarningLabel")}
                            </strong>
                        </div>
                    </div>
                    <div className="row stack-buffer">
                        <div className={this.isFormInvalid() ? "col-lg-12 col-md-12 col-sm-12 has-error" : "col-lg-12 col-md-12 col-sm-12"}>
                            <div className="divlabel">
                                <label className="no-margin">
                                    {cacheStore.GetString("NoteDeletionReasonLabel")}
                                </label>
                            </div>
                            <Combobox
                                HasEmptyItem={true}
                                id="_delayComboBox"
                                items={this.noteDeletionReasons
                                    .map((deletionReason, idx) => new ComboBoxDropDownItem(deletionReason, cacheStore.GetString(deletionReason), idx))
                                    .toImmutable()
                                }
                                selectedCode={activeCode}
                                onChange={this.onComboBoxChange.bind(this)}
                            />
                        </div>
                    </div>
                    <div className="row stack-buffer">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <textarea
                                className="form-control fixed-size"
                                value={this.state.details}
                                onChange={this.onTextboxChange.bind(this)}
                                rows={4}
                            />
                        </div>
                    </div>
                </div>
                <div className="p-footer">
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12 columns text-right">
                            <button
                                data-action="cancel"
                                className="btn btn-default push-right-5"
                                onClick={this.cancelAndClosePopup}
                            >
                                {cacheStore.GetString("CancelLabel")}
                            </button>
                            <button
                                data-cy="medicalNote-DeleteReason-popup-confirm"
                                className="btn btn-danger"
                                disabled={this.isSubmitDisabled()}
                                onClick={() => this.acceptAndConfirm(currentEncounter.Id, currentEncounter.PatientId)}
                            >
                                {cacheStore.GetString("ConfirmLabel")}
                            </button>
                        </div>
                    </div>
                </div>
            </Popup>
        );
    }

    private onTextboxChange(e: React.ChangeEvent<HTMLTextAreaElement>): void {
        this.mutateState(s => {
            s.details = e.currentTarget.value;
        });
    }

    private onComboBoxChange(code: string): void {
        this.mutateState(s => {
            s.reasonCode = code;
        });
    }

    private acceptAndConfirm(encounterId: string, patientId: string): void {
        this.mutateState(s => {
            s.isLoading = true;
        });

        sessionStore.dispatch(LocalPatientOperations.MedicalNote.DeleteNoteOK, { encounterId: encounterId, patientId: patientId, reasonCode: this.state.reasonCode, reasondetail: this.state.details });
    }

    private cancelAndClosePopup(): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.DeleteNoteCancel);
    }

    private isFormInvalid(): boolean {
        return (this.state.reasonCode == "");
    }

    private isSubmitDisabled(): boolean {
        return this.state.isLoading || this.isFormInvalid();
    }

    private mutateState(mutation: (s: MedicalNoteDeleteWarningPopupState) => void, then: any = null): void {
        let s = new MedicalNoteDeleteWarningPopupState();
        mutation(s);
        this.setState(s, then);
    }
}

export { MedicalNoteDeleteReasonPopup };
